import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import { Botao } from '../../../web/src/componentes/base';
import { BotaoLerEmVoz } from '../../../web/src/componentes/leitura-voz';
import { Modal } from '../../../web/src/componentes/sobreposicao';
import { mostrarDeNovo, registrarPrimeiraVisita, type TelaAjuda } from '../ajuda';

/**
 * U15 F2: o estado da ajuda da tela atual. Na primeira visita a cada tela o painel abre sozinho uma vez (a marca fica no
 * aparelho); `automatico` é falso na visualização da equipe (só leitura), que nunca abre sozinho. Trocar de tela fecha o painel.
 */
export function useAjuda(tela: TelaAjuda | null, automatico: boolean) {
  const [aberto, setAberto] = useState(false);
  // A tela já tratada: o StrictMode roda o efeito duas vezes e a segunda não pode fechar o que a primeira abriu.
  const tratada = useRef<TelaAjuda | null | undefined>(undefined);
  useEffect(() => {
    if (tratada.current === tela) return;
    tratada.current = tela;
    setAberto(!!tela && automatico && registrarPrimeiraVisita(tela));
  }, [tela, automatico]);
  return { aberto, abrir: () => setAberto(true), fechar: () => setAberto(false) };
}

/** U15 F1: botão "Ajuda" do cabeçalho (ícone mais o texto visível), no padrão `Botao` do portal. */
export function BotaoAjuda({ aoClicar, className = '' }: { aoClicar: () => void; className?: string }) {
  const { t } = useTranslation('portal');
  return (
    <Botao variante="fantasma" tamanho="sm" className={className} icone={<CircleHelp className="size-4" aria-hidden />} onClick={aoClicar} data-ajuda="">
      {t('ajuda.botao')}
    </Botao>
  );
}

/**
 * U15 F1: o painel com a ajuda da tela, no `Modal` que o portal já usa (foco preso e devolvido, Esc fecha). Três blocos:
 * "Para que serve", "Como fazer" (passos numerados) e "Se der errado". F2: "Mostrar de novo na próxima visita" limpa a marca.
 */
export function PainelAjuda({ tela, aberto, aoFechar }: { tela: TelaAjuda; aberto: boolean; aoFechar: () => void }) {
  const { t, i18n } = useTranslation('portal');
  const [limpou, setLimpou] = useState(false);
  // U15 F4: "Ler em voz alta" lê o texto do painel (para, ao fechar, no desmonte do botão).
  const texto = useRef<HTMLDivElement>(null);
  useEffect(() => setLimpou(false), [tela, aberto]);
  const passos = t(`ajuda.${tela}.passos`, { returnObjects: true }) as unknown as string[];
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('ajuda.titulo', { tela: t(`menu.${tela}`) })}
      rodape={
        <>
          <Botao variante="fantasma" ocupado={limpou} onClick={() => setLimpou(mostrarDeNovo(tela))} data-ajuda-de-novo="">
            {t('ajuda.deNovo')}
          </Botao>
          <Botao variante="primario" onClick={aoFechar}>
            {t('ajuda.entendi')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-sm" data-ajuda-tela={tela}>
        <div className="self-start">
          <BotaoLerEmVoz alvo={texto} idioma={i18n.language} rotuloLer={t('ajuda.lerEmVoz')} rotuloParar={t('ajuda.pararLeitura')} />
        </div>
        <div ref={texto} className="flex flex-col gap-4">
        <section>
          <h3 className="kicker">{t('ajuda.paraQue')}</h3>
          <p className="mt-1 text-tinta-2">{t(`ajuda.${tela}.para`)}</p>
        </section>
        <section>
          <h3 className="kicker">{t('ajuda.como')}</h3>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-tinta-2">
            {passos.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
        </section>
        <section>
          <h3 className="kicker">{t('ajuda.erro')}</h3>
          <p className="mt-1 text-tinta-2">{t(`ajuda.${tela}.erro`)}</p>
        </section>
        </div>
        <p role="status" className="text-xs text-tinta-3">
          {limpou ? t('ajuda.deNovoFeito') : ''}
        </p>
      </div>
    </Modal>
  );
}
